const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

function getDeviceFingerprint() {
  try {
    const key = 'aot_device_id';
    let id = localStorage.getItem(key);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return '';
  }
}

class ApiService {
  constructor() {
    // Eski localStorage token'larını temizle (httpOnly cookie'ye geçiş)
    try {
      localStorage.removeItem('token');
    } catch {
      /* ignore */
    }
    this.token = null;
  }

  setToken(_token) {
    // JWT artık httpOnly cookie'de; localStorage kullanılmıyor
    this.token = null;
  }

  removeToken() {
    this.token = null;
    try {
      localStorage.removeItem('token');
    } catch {
      /* ignore */
    }
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': getDeviceFingerprint(),
        ...options.headers,
      },
      ...options,
    };

    if (config.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type') || '';
      let data;

      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        const preview = text.slice(0, 200).replace(/\s+/g, ' ').trim();
        throw new Error(
          response.ok
            ? 'Sunucu geçersiz yanıt döndürdü (JSON bekleniyordu).'
            : `Sunucu hatası (${response.status}): ${preview || response.statusText}`
        );
      }

      if (!response.ok) {
        throw new Error(data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth API
  async login(email, password) {
    const result = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, deviceFingerprint: getDeviceFingerprint() }),
    });
    return result;
  }

  async register(name, email, password) {
    const result = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, deviceFingerprint: getDeviceFingerprint() }),
    });
    return result;
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  async verifyEmail(code) {
    return this.request('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async resendVerificationCode() {
    return this.request('/api/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async forgotPassword(email) {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(email, code, password) {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, code, password }),
    });
  }

  async updateThemePreference(theme) {
    return this.request('/api/auth/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ theme }),
    });
  }

  async updateProfile(body) {
    return this.request('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async uploadAvatar(formData) {
    const response = await fetch(`${API_BASE_URL}/api/auth/avatar`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Device-Id': getDeviceFingerprint(),
      },
      body: formData,
    });
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : { error: await response.text() };
    if (!response.ok) {
      throw new Error(data.error || 'Avatar yüklenemedi');
    }
    return data;
  }

  async getUserStats() {
    return this.request('/api/auth/stats');
  }

  async getSessions() {
    return this.request('/api/auth/sessions');
  }

  async deleteSession(sessionId) {
    return this.request(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' });
  }

  async logoutAll() {
    return this.request('/api/auth/logout-all', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  logout() {
    this.removeToken();
    return this.request('/api/auth/logout', { method: 'POST', body: JSON.stringify({}) }).catch(() => {});
  }

  // Notes API
  async getNotes(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.subject) query.set('subject', params.subject);
    if (params.universityYear) query.set('universityYear', params.universityYear);
    if (params.university) query.set('university', params.university);
    if (params.department) query.set('department', params.department);
    if (params.minDownloads !== undefined && params.minDownloads !== null) {
      query.set('minDownloads', String(params.minDownloads));
    }
    if (params.sort) query.set('sort', params.sort);
    if (params.search) query.set('search', params.search);

    const qs = query.toString();
    return this.request(`/api/notes${qs ? '?' + qs : ''}`);
  }

  async getMyNotes(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.status) query.set('status', params.status);
    const qs = query.toString();
    return this.request(`/api/notes/mine${qs ? '?' + qs : ''}`);
  }

  async getNotesStatsSummary() {
    return this.request('/api/notes/stats/summary');
  }

  async getSiteSettings() {
    return this.request('/api/site-settings');
  }

  async getForumThreads(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.category) query.set('category', params.category);
    const qs = query.toString();
    return this.request(`/api/forum/threads${qs ? '?' + qs : ''}`);
  }

  async getForumThread(id) {
    return this.request(`/api/forum/threads/${id}`);
  }

  async createForumThread(body) {
    return this.request('/api/forum/threads', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async createForumReply(threadId, body) {
    return this.request(`/api/forum/threads/${threadId}/replies`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteForumThread(threadId) {
    return this.request(`/api/forum/threads/${threadId}`, { method: 'DELETE' });
  }

  async getForumStats() {
    return this.request('/api/forum/stats/summary');
  }

  async getNote(id) {
    return this.request(`/api/notes/${id}`);
  }

  async createNote(noteData) {
    return this.request('/api/notes', {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  }

  async updateNote(id, noteData) {
    return this.request(`/api/notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(noteData),
    });
  }

  async deleteNote(id) {
    return this.request(`/api/notes/${id}`, {
      method: 'DELETE',
    });
  }

  async rateNote(noteId, rating, feedback) {
    return this.request(`/api/notes/${noteId}/rate`, {
      method: 'POST',
      body: JSON.stringify({
        rating,
        feedback: feedback || '',
        deviceFingerprint: getDeviceFingerprint(),
      }),
    });
  }

  async incrementNoteDownload(noteId) {
    return this.request(`/api/notes/${noteId}/download`, { method: 'POST' });
  }

  async downloadNoteFile(noteId, fileName = 'not.pdf', fileUrl = '') {
    const url = `${API_BASE_URL}/api/notes/${noteId}/file`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'X-Device-Id': getDeviceFingerprint() },
    });

    if (!response.ok) {
      let message = 'İndirme başarısız';
      let fallbackUrl = fileUrl;
      try {
        const data = await response.json();
        if (data.error) message = data.error;
        if (data.fallbackUrl) fallbackUrl = data.fallbackUrl;
      } catch {
        /* ignore */
      }

      if (fallbackUrl) {
        const { downloadFile } = await import('../utils/downloadHelper');
        await downloadFile(fallbackUrl, fileName);
        return;
      }

      throw new Error(message);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName || 'not.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  // Upload API
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    return fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Device-Id': getDeviceFingerprint(),
      },
      body: formData,
    }).then(res => res.json());
  }

  /** Forum görselleri: sunucu imgbb proxy (IMGBB_API_KEY backend .env) */
  async uploadImageImgbb(file) {
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${API_BASE_URL}/api/upload/imgbb`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Device-Id': getDeviceFingerprint(),
      },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Görsel yüklenemedi');
    }
    return data;
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Admin API
  async adminGetStats() {
    return this.request('/api/admin/stats');
  }

  async adminGetUsers(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    const qs = query.toString();
    return this.request(`/api/admin/users${qs ? '?' + qs : ''}`);
  }

  async adminChangeRole(userId, role) {
    return this.request(`/api/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async adminDeactivateUser(userId) {
    return this.request(`/api/admin/users/${userId}`, { method: 'DELETE' });
  }

  async adminGetFeedbacks(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.request(`/api/admin/feedbacks${qs ? `?${qs}` : ''}`);
  }

  async adminUpdateUser(userId, body) {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async adminSetUserPassword(userId, password) {
    return this.request(`/api/admin/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  }

  async adminBanUser(userId, reason) {
    return this.request(`/api/admin/users/${userId}/ban`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    });
  }

  async adminGetNotes(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.status) query.set('status', params.status);
    const qs = query.toString();
    return this.request(`/api/admin/notes${qs ? '?' + qs : ''}`);
  }

  async adminApproveNote(noteId) {
    return this.request(`/api/admin/notes/${noteId}/approve`, { method: 'PUT' });
  }

  async adminRejectNote(noteId) {
    return this.request(`/api/admin/notes/${noteId}/reject`, { method: 'PUT' });
  }

  async adminDeleteNote(noteId) {
    return this.request(`/api/admin/notes/${noteId}`, { method: 'DELETE' });
  }

  async adminGetSiteSettings() {
    return this.request('/api/admin/site-settings');
  }

  async adminPutSiteSettings(body) {
    return this.request('/api/admin/site-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async adminQuickAddNotes(body) {
    return this.request('/api/admin/notes/quick', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Google Drive admin
  async adminGoogleStatus() {
    return this.request('/api/admin/google/status');
  }

  async adminGoogleAuthUrl() {
    return this.request('/api/admin/google/auth-url');
  }

  async adminGooglePickerToken() {
    return this.request('/api/admin/google/picker-token');
  }

  async adminGoogleDisconnect() {
    return this.request('/api/admin/google/disconnect', { method: 'POST' });
  }

  async adminGoogleImport(fileIds, autoApprove = true) {
    return this.request('/api/admin/google/import', {
      method: 'POST',
      body: JSON.stringify({ fileIds, autoApprove }),
    });
  }

  async adminGooglePreview(fileId) {
    return this.request(`/api/admin/google/files/${fileId}/preview`);
  }

  async adminGoogleSeedCatalog() {
    return this.request('/api/admin/google/course-catalog/seed', { method: 'POST' });
  }

  // Member Drive upload
  async getDriveUploadSession() {
    return this.request('/api/notes/drive-upload-session');
  }

  async createNoteFromDrive(payload) {
    return this.request('/api/notes/from-drive', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Notifications
  async getNotifications() {
    return this.request('/api/notifications');
  }

  async getUnreadCelebration() {
    return this.request('/api/notifications/unread-celebration');
  }

  async markNotificationRead(id) {
    return this.request(`/api/notifications/${id}/read`, { method: 'PATCH' });
  }

  // Gamification (legacy components — graceful stubs until backend routes exist)
  async getUserBadges() {
    return { success: true, data: { earned: [], available: [] } };
  }

  async getDailyQuests() {
    return { success: true, data: { quests: [], completedCount: 0, totalCount: 0 } };
  }

  async getLeaderboard(category, period, params = {}) {
    const query = new URLSearchParams({ category, period, ...params });
    try {
      return await this.request(`/api/gamification/leaderboard?${query}`);
    } catch {
      return { success: true, data: { leaderboard: [], userRank: null } };
    }
  }

  async getUserRank(category, period) {
    try {
      return await this.request(`/api/gamification/rank?category=${category}&period=${period}`);
    } catch {
      return { success: true, data: { rank: null } };
    }
  }

  // Daily questions (legacy)
  async getDailyQuestions(params = {}) {
    const query = new URLSearchParams(params);
    try {
      return await this.request(`/api/daily-questions?${query}`);
    } catch {
      return { success: true, data: { questions: [] } };
    }
  }

  async submitAnswer(questionId, answerText) {
    return this.request(`/api/daily-questions/${questionId}/answers`, {
      method: 'POST',
      body: JSON.stringify({ answer: answerText }),
    });
  }

  async likeQuestion(questionId) {
    return this.request(`/api/daily-questions/${questionId}/like`, { method: 'POST' });
  }

  async likeAnswer(questionId, answerId) {
    return this.request(`/api/daily-questions/${questionId}/answers/${answerId}/like`, { method: 'POST' });
  }

  // Community feed (legacy)
  async getCommunityPosts(params = {}) {
    const query = new URLSearchParams(params);
    return this.request(`/api/community/posts?${query}`);
  }

  async createCommunityPost(body) {
    return this.request('/api/community/posts', { method: 'POST', body: JSON.stringify(body) });
  }

  async likeCommunityPost(postId) {
    return this.request(`/api/community/posts/${postId}/like`, { method: 'POST' });
  }

  async deleteCommunityPost(postId) {
    return this.request(`/api/community/posts/${postId}`, { method: 'DELETE' });
  }

  async updateCommunityPost(postId, body) {
    return this.request(`/api/community/posts/${postId}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async pinCommunityPost(postId) {
    return this.request(`/api/community/posts/${postId}/pin`, { method: 'POST' });
  }

  async reportCommunityPost(postId, reason) {
    return this.request(`/api/community/posts/${postId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async addCommentToPost(postId, content) {
    return this.request(`/api/community/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async addReplyToComment(postId, commentId, content) {
    return this.request(`/api/community/posts/${postId}/comments/${commentId}/replies`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async createPoll(postId, pollData) {
    return this.request(`/api/community/posts/${postId}/poll`, {
      method: 'POST',
      body: JSON.stringify(pollData),
    });
  }

  async votePoll(postId, optionIndex) {
    return this.request(`/api/community/posts/${postId}/poll/vote`, {
      method: 'POST',
      body: JSON.stringify({ optionIndex }),
    });
  }

  async getCategories() {
    try {
      return await this.request('/api/notes/categories');
    } catch {
      return { success: true, data: { categories: [] } };
    }
  }

  async downloadNote(noteId) {
    return this.downloadNoteFile(noteId);
  }
}

const api = new ApiService();
export default api;
